/**
 * Road-closure coverage vocabulary (#337) — the SINGLE source of every operator-facing
 * string the honest data-coverage surface adds. Centralized so
 * `roadClosureCoverageVocabulary.lock.test.ts` can reflectively prove none of it drifts
 * into a forbidden group (US-ICS / false-all-clear / own-family), the same guard the
 * #325 api-health + #563 feed vocab locks use.
 *
 * Honesty invariant (the whole point of #337): the copy states plainly WHERE the
 * road-closure feed reaches (Ontario · Alberta · British Columbia) and keeps
 * absence-of-FEED visibly distinct from a covered province that genuinely has zero
 * closures. It NEVER implies an all-clear and NEVER asserts a feed exists where it
 * does not. Plain-language throughout — no reserved IMS / US-ICS term.
 */
import {
  COVERED_PROVINCE_NAMES,
  type RoadClosureCoverageState,
} from './roadClosureCoverage';

/** The covered provinces rendered as a plain-language list ("Ontario, Alberta &
 *  British Columbia"), built from the covered set so the copy can't drift from it. */
export function coveredProvincesPhrase(): string {
  const names = [...COVERED_PROVINCE_NAMES];
  if (names.length <= 1) return names.join('');
  const last = names[names.length - 1];
  return `${names.slice(0, -1).join(', ')} & ${last}`;
}

/**
 * The STANDING layer-level note (always shown while the Road Closures layer is on).
 * Scopes the feed and states the unknown-≠-zero truth outright, so an empty layer
 * elsewhere never reads as "no closures here".
 */
export const ROAD_CLOSURE_COVERAGE_NOTE = `Road-closure feed covers ${coveredProvincesPhrase()} only. Elsewhere the map has no feed here — that is not the same as "no closures".`;

/**
 * Per-view cue when the operator is viewing a region OUTSIDE the feed's coverage.
 * Absence of FEED — the map cannot answer here, so silence is not "zero".
 */
export const ROAD_CLOSURE_NO_FEED_HERE = `No road-closure feed for this area — coverage is ${coveredProvincesPhrase()} only.`;

/**
 * Per-view cue when the operator is viewing a COVERED province whose in-view closure
 * count is a known ZERO — genuinely nothing to report right now, with the feed live.
 * Deliberately distinct from the no-feed cue (the unknown-≠-zero core).
 */
export const ROAD_CLOSURE_NONE_REPORTED = 'No road closures reported here — feed is live.';

/**
 * Map a coverage state to the per-view cue string, or null when the state warrants no
 * cue beyond the standing note (`live-closures` → the map shows them; `unknown` → we
 * don't yet know, so assert nothing).
 */
export function roadClosureCoverageCue(state: RoadClosureCoverageState): string | null {
  switch (state) {
    case 'no-feed':
      return ROAD_CLOSURE_NO_FEED_HERE;
    case 'live-none':
      return ROAD_CLOSURE_NONE_REPORTED;
    case 'live-closures':
    case 'unknown':
    default:
      return null;
  }
}
